/**
 * @fileoverview Card component for the Storyteller web application.
 * Displays a card with a background color and optional animation.
 *
 * @example
 * // Basic usage
 * <Card variant="low" animate={true}>
 *     <div>Card content</div>
 * </Card>
 */

import css from './Card.module.css'


/**
 * @interface CardProps
 * @description Props for the Card component.
 * @property {('low'|'medium'|'high')} variant - The shadow variant of the card.
 * @property {boolean} animate - Whether to animate the card entrance.
 * @property {React.ReactNode} children - The content to render inside the card.
 * @property {string} [className] - Additional CSS class names to apply.
 * @extends {React.HTMLAttributes<HTMLDivElement>}
 */

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
    variant: 'low' | 'medium' | 'high'
    animate: boolean
}

/**
 * @function Card
 * @description Component for displaying a card with a background color and optional animation.
 * @param {CardProps} props - The props for the Card component.
 * @returns {JSX.Element} The Card component.
 */
export function Card({ variant, animate, children, className }: CardProps) {
    return (
        <div className={`${css.card} ${css[variant]} ${animate && css.animate} ${className}`}>
            {children}
        </div>
    )
}
